import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { apiFetch, setLogoutHandler } from "./api";
import { EXERCISE_CATALOGUE } from "./constants";
import { Icon } from "./icons";
import css from "./css";

import LoginPage       from "./views/LoginPage";
import DashboardView   from "./views/DashboardView";
import ExercisesView   from "./views/ExercisesView";
import WorkoutsView    from "./views/WorkoutsView";
import ProgressView    from "./views/ProgressView";
import LeaderboardView from "./views/LeaderboardView";
import AdminView       from "./views/AdminView";
import ProfileView     from "./views/ProfileView";

import ExerciseModal          from "./components/ExerciseModal";
import WorkoutModal          from "./components/WorkoutModal";
import CataloguePickerModal  from "./components/CataloguePickerModal";

function BellIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

function AppInner({ user, isAdmin, isSuperAdmin, exercises, workouts, handleLogout, handleLogSession, handleSaveEx, handleDeleteEx, handleDeleteSet, handleSaveWk, handleDeleteWk, handleSaveName, handleSyncCatalogue, units, handleUnitsChange }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [showExModal, setShowExModal] = useState(false);
  const [showWkModal, setShowWkModal] = useState(false);
  const [showCatPicker, setShowCatPicker] = useState(false);
  const [editEx, setEditEx] = useState(null);
  const [editWk, setEditWk] = useState(null);

  const currentPath = location.pathname;

  const topbarBtn = {
    "/exercises": { label:"+ Exercise", action:()=> setShowCatPicker(true) },
    "/workouts":  { label:"+ Workout",  action:()=>{ setEditWk(null); setShowWkModal(true); } },
  }[currentPath];

  const bottomNavItems = [
    { path:"/",            label:"DASHBOARD", Icon:Icon.Dashboard  },
    { path:"/exercises",   label:"EXERCISES", Icon:Icon.Dumbbell   },
    { path:"/workouts",    label:"WORKOUTS",  Icon:Icon.Workout    },
    { path:"/progress",    label:"PROGRESS",  Icon:Icon.TrendingUp },
    { path:"/leaderboard", label:"RANKS",     Icon:Icon.Trophy     },
    ...(isAdmin ? [{ path:"/admin", label:"ADMIN", Icon:Icon.Shield }] : []),
  ];

  return (
    <div className="app">
      {/* ── TOPBAR ── */}
      <header style={{
        background: "#0c0e12",
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        height: 64,
        borderBottom: "1px solid rgba(30,41,59,0.5)",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 24px", height: "100%", maxWidth: 448, margin: "0 auto",
        }}>
          {/* Left: Avatar */}
          <div
            onClick={() => navigate("/profile")}
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: "#353534",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 11, fontWeight: 900, color: "#b9c8de",
              cursor: "pointer", flexShrink: 0,
              border: "1px solid rgba(68,71,76,0.4)",
              overflow: "hidden",
            }}
          >
            {user.slice(0,2).toUpperCase()}
          </div>

          {/* Center: Branding */}
          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 900, fontSize: 22,
            letterSpacing: "-0.04em",
            textTransform: "uppercase",
            color: "#94a3b8",
          }}>GIGA MONKY</h1>

          {/* Right: Action or Bell */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {topbarBtn && (
              <button
                onClick={topbarBtn.action}
                style={{
                  padding: "6px 14px", borderRadius: 6, border: "none",
                  background: "#94a3b8", color: "#233143",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
                  cursor: "pointer", textTransform: "uppercase",
                  transition: "opacity 0.15s",
                }}
              >{topbarBtn.label}</button>
            )}
          </div>
        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      <div className="content">
        <Routes>
          <Route path="/"            element={<DashboardView   exercises={exercises} workouts={workouts} onLogSession={handleLogSession} units={units}/>}/>
          <Route path="/exercises"   element={<ExercisesView   exercises={exercises} onEdit={ex=>{setEditEx(ex);setShowExModal(true);}} onDelete={handleDeleteEx} onDeleteSet={handleDeleteSet} units={units}/>}/>
          <Route path="/workouts"    element={<WorkoutsView    workouts={workouts} allExercises={exercises} onEdit={wk=>{setEditWk(wk);setShowWkModal(true);}} onDelete={handleDeleteWk} onLogSession={handleLogSession} units={units}/>}/>
          <Route path="/progress"    element={<ProgressView    exercises={exercises} units={units}/>}/>
          <Route path="/leaderboard" element={<LeaderboardView user={user} units={units}/>}/>
          <Route path="/admin"       element={isAdmin ? <AdminView isSuperAdmin={isSuperAdmin}/> : <Navigate to="/" replace/>}/>
          <Route path="/profile"     element={<ProfileView user={user} isAdmin={isAdmin} onLogout={handleLogout} onSave={handleSaveName} units={units} onUnitsChange={handleUnitsChange}/>}/>
          <Route path="*"            element={<Navigate to="/" replace/>}/>
        </Routes>
      </div>

      {/* ── BOTTOM NAV ── */}
      <nav style={{
        position: "fixed",
        bottom: 40,
        left: 16, right: 16,
        zIndex: 50,
        background: "rgba(18,18,20,0.88)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 16,
        display: "flex", alignItems: "center", justifyContent: "space-around",
        padding: "10px 8px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
      }}>
        {bottomNavItems.map(n => {
          const isActive = currentPath === n.path;
          return (
            <div
              key={n.path}
              onClick={() => navigate(n.path)}
              style={{
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 4, padding: "4px 8px",
                cursor: "pointer", transition: "color 0.15s",
                color: isActive ? "#f1f5f9" : "#555c6a",
                flex: 1,
              }}
            >
              <n.Icon />
              <span style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700, fontSize: 9, letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}>{n.label}</span>
            </div>
          );
        })}
      </nav>

      {/* ── MODALS ── */}
      {showExModal && <ExerciseModal existing={editEx} allExercises={exercises} units={units} onClose={()=>{setShowExModal(false);setEditEx(null);}} onSave={handleSaveEx}/>}
      {showWkModal && <WorkoutModal  existing={editWk} allExercises={exercises} onClose={()=>{setShowWkModal(false);setEditWk(null);}} onSave={handleSaveWk}/>}
      {showCatPicker && <CataloguePickerModal exercises={exercises} onAdd={ex=>{ handleSaveEx(ex); setShowCatPicker(false); }} onClose={()=>setShowCatPicker(false)}/>}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(() => localStorage.getItem("ft_username"));
  const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem("ft_isAdmin") === "true");
  const [isSuperAdmin, setIsSuperAdmin] = useState(() => localStorage.getItem("ft_isSuperAdmin") === "true");
  const [exercises, setExercises] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState(() => localStorage.getItem("ft_units") || "lbs");

  const handleUnitsChange = (u) => { setUnits(u); localStorage.setItem("ft_units", u); };

  useEffect(() => {
    const t = localStorage.getItem("ft_theme") || "dark";
    document.documentElement.setAttribute("data-theme", t);
  }, []);

  useEffect(() => {
    setLogoutHandler(handleLogout);
  }); // no deps — always keep the handler pointing at the current closure

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async (attempt = 0) => {
      setLoading(true);
      try {
        const [exs, wks] = await Promise.all([apiFetch("/exercises"), apiFetch("/workouts")]);
        if (!cancelled) { setExercises(exs); setWorkouts(wks); }
      } catch (err) {
        if (err.error === "Invalid token" || err.error === "No token" || err.error === "Session expired") { handleLogout(); return; }
        // Backend may still be starting up after a redeploy — retry up to 4 times
        if (!cancelled && attempt < 4) setTimeout(() => load(attempt + 1), 1500);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user]);

  function _storeAuth(data) {
    localStorage.setItem("ft_token", data.token);
    if (data.refreshToken) localStorage.setItem("ft_refresh", data.refreshToken);
    localStorage.setItem("ft_username", data.name);
    localStorage.setItem("ft_isAdmin", data.isAdmin);
    localStorage.setItem("ft_isSuperAdmin", data.isSuperAdmin);
    setUser(data.name);
    setIsAdmin(!!data.isAdmin);
    setIsSuperAdmin(!!data.isSuperAdmin);
  }

  const handleLogin = async (email, password) => {
    const data = await apiFetch("/login", { method: "POST", body: { email, password } });
    _storeAuth(data);
  };

  const handleRegister = async (email, password, name, adminKey, gender, birthday) => {
    const data = await apiFetch("/register", { method: "POST", body: { email, password, name, adminKey, gender, birthday } });
    _storeAuth(data);
  };

  const handleGoogle = useCallback(async (credential) => {
    const data = await apiFetch("/auth/google", { method: "POST", body: { credential } });
    _storeAuth(data);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    const refreshToken = localStorage.getItem("ft_refresh");
    if (refreshToken) {
      apiFetch("/auth/logout", { method: "POST", body: { refreshToken } }).catch(() => {});
    }
    localStorage.removeItem("ft_token");
    localStorage.removeItem("ft_refresh");
    localStorage.removeItem("ft_username");
    localStorage.removeItem("ft_isAdmin");
    localStorage.removeItem("ft_isSuperAdmin");
    setUser(null); setIsAdmin(false); setIsSuperAdmin(false);
    setExercises([]); setWorkouts([]);
  };

  const handleSaveEx = async (ex) => {
    try {
      const isNew = !exercises.find(e => String(e.id) === String(ex.id));
      if (isNew) {
        const saved = await apiFetch("/exercises", { method: "POST", body: ex });
        setExercises(prev => [...prev, saved]);
      } else {
        // Preserve historical sets — edit modal only changes name/muscles/1RM/notes
        const current = exercises.find(e => String(e.id) === String(ex.id));
        const payload = { ...ex, sets: current?.sets || [] };
        await apiFetch(`/exercises/${ex.id}`, { method: "PUT", body: payload });
        setExercises(prev => prev.map(e => String(e.id) === String(ex.id) ? { ...e, ...payload } : e));
      }
    } catch (err) {
      console.error("handleSaveEx error:", err);
      alert("Failed to save exercise: " + (err.error || err.message || JSON.stringify(err)));
    }
  };

  const handleSyncCatalogue = async () => {
    let updated = 0;
    for (const ex of exercises) {
      const match = EXERCISE_CATALOGUE.find(c => c.name.toLowerCase() === ex.name.toLowerCase());
      if (!match) continue;
      if (JSON.stringify(ex.muscles) === JSON.stringify(match.muscles) && ex.category === match.category) continue;
      try {
        await apiFetch(`/exercises/${ex.id}`, { method: "PUT", body: {
          ...ex, muscles: match.muscles, muscle: match.muscles[0], category: match.category,
        }});
        updated++;
      } catch (e) { console.error(e); }
    }
    const fresh = await apiFetch("/exercises");
    setExercises(fresh);
    alert(`Synced ${updated} exercise${updated !== 1 ? "s" : ""} with catalogue.`);
  };

  const handleDeleteSet = async (setId, exerciseId) => {
    try {
      await apiFetch(`/sets/${setId}`, { method: "DELETE" });
      setExercises(prev => prev.map(e =>
        e.id === exerciseId ? { ...e, sets: e.sets.filter(s => s.id !== setId) } : e
      ));
    } catch (err) { console.error(err); }
  };

  const handleDeleteEx = async (id) => {
    try {
      await apiFetch(`/exercises/${id}`, { method: "DELETE" });
      setExercises(prev => prev.filter(e => e.id !== id));
    } catch (err) { console.error(err); }
  };

  const handleLogSession = async ({ exercises: sessionExercises }) => {
    for (const item of sessionExercises) {
      if (!item.sets.length) continue;
      const primary = item.muscles?.[0] || "Full Body";
      const existing = exercises.find(e => e.name.toLowerCase() === item.name.toLowerCase());
      if (existing) {
        try {
          await apiFetch(`/exercises/${existing.id}/log`, { method: "POST", body: item.sets });
          setExercises(prev => prev.map(e =>
            e.id === existing.id ? { ...e, sets: [...(e.sets || []), ...item.sets] } : e
          ));
        } catch (err) { console.error(err); }
      } else {
        const newEx = {
          name: item.name, muscle: primary, muscles: item.muscles || [primary],
          sets: item.sets, category: "", oneRepMax: null,
        };
        try {
          const saved = await apiFetch("/exercises", { method: "POST", body: newEx });
          setExercises(prev => [...prev, { ...newEx, id: saved.id }]);
        } catch (err) { console.error(err); }
      }
    }
  };

  const handleSaveWk = async (wk) => {
    try {
      // Resolve any catalogue exercises (cat__ IDs) to real DB IDs
      let currentExercises = exercises;
      const resolvedIds = await Promise.all((wk.exercises || []).map(async exId => {
        if (typeof exId !== "string" || !exId.startsWith("cat__")) return exId;
        const name = exId.slice(5);
        const existing = currentExercises.find(e => e.name.toLowerCase() === name.toLowerCase());
        if (existing) return existing.id;
        const catEx = EXERCISE_CATALOGUE.find(e => e.name === name);
        const saved = await apiFetch("/exercises", { method: "POST", body: {
          name, muscle: catEx?.muscles?.[0] || "Full Body",
          muscles: catEx?.muscles || ["Full Body"],
          sets: [], category: catEx?.category || "", oneRepMax: null,
        }});
        currentExercises = [...currentExercises, saved];
        setExercises(currentExercises);
        return saved.id;
      }));
      const resolved = { ...wk, exercises: resolvedIds };
      const isNew = !workouts.find(w => w.id === wk.id);
      if (isNew) {
        const saved = await apiFetch("/workouts", { method: "POST", body: resolved });
        setWorkouts(prev => [...prev, { ...resolved, id: saved.id }]);
      } else {
        await apiFetch(`/workouts/${wk.id}`, { method: "PUT", body: resolved });
        setWorkouts(prev => prev.map(w => w.id === wk.id ? resolved : w));
      }
    } catch (err) { console.error(err); }
  };

  const handleDeleteWk = async (id) => {
    try {
      await apiFetch(`/workouts/${id}`, { method: "DELETE" });
      setWorkouts(prev => prev.filter(w => w.id !== id));
    } catch (err) { console.error(err); }
  };

  const handleSaveName = (name) => {
    setUser(name);
    localStorage.setItem("ft_username", name);
  };

  if (!user) return (
    <>
      <style>{css}</style>
      <LoginPage onLogin={handleLogin} onRegister={handleRegister} onGoogle={handleGoogle}/>
    </>
  );

  if (loading) return (
    <>
      <style>{css}</style>
      <div className="loading-screen">
        <div style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:22,fontWeight:900,letterSpacing:"-0.04em",textTransform:"uppercase",color:"#94a3b8",marginBottom:16}}>GIGA MONKY</div>
        <div className="label-caps" style={{marginBottom:12}}>LOADING</div>
        <div style={{display:"flex",gap:6}}>
          <span className="loading-dot"/>
          <span className="loading-dot"/>
          <span className="loading-dot"/>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      <BrowserRouter>
        <AppInner
          user={user}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          exercises={exercises}
          workouts={workouts}
          handleLogout={handleLogout}
          handleLogSession={handleLogSession}
          handleSaveEx={handleSaveEx}
          handleDeleteEx={handleDeleteEx}
          handleDeleteSet={handleDeleteSet}
          handleSaveWk={handleSaveWk}
          handleDeleteWk={handleDeleteWk}
          handleSaveName={handleSaveName}
          handleSyncCatalogue={handleSyncCatalogue}
          units={units}
          handleUnitsChange={handleUnitsChange}
        />
      </BrowserRouter>
    </>
  );
}
